import React from 'react';
import { FileUpload } from '@ark-ui/react/file-upload';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface FileUploadInputProps extends BaseInputProps<File[]> {
  /** Akceptované MIME typy / přípony (předáno do nativního inputu). */
  accept?: string;
  /** Maximální počet souborů; default 1, resp. neomezeně při `multiple`. */
  maxFiles?: number;
  /** Povolit výběr více souborů. */
  multiple?: boolean;
  /** Text spouštěče výběru souborů. */
  triggerLabel?: string;
  /** Text drop zóny. */
  dropzoneLabel?: string;
  className?: string;
}

/**
 * Atomický nahrávač souborů postavený na Ark UI `FileUpload`.
 *
 * RHF drží hodnotu jako pole `File[]` a komponenta ji předává jako controlled
 * `acceptedFiles` — `reset()`/`setValue()` z RHF se tak propíše do UI (jinak
 * by po resetu formuláře soubory zůstaly viset v seznamu).
 */
export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  accept,
  maxFiles,
  multiple,
  triggerLabel = 'Vybrat soubor(y)',
  dropzoneLabel = 'Sem přetáhněte soubory',
  className,
}) => (
  <FileUpload.Root
    name={name}
    accept={accept}
    // `multiple` bez explicitního `maxFiles` = prakticky neomezeno.
    maxFiles={maxFiles ?? (multiple ? Number.MAX_SAFE_INTEGER : 1)}
    acceptedFiles={value ?? []}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    onFileChange={(details) => {
      onChange(details.acceptedFiles);
      // Výběr/zahození souboru = dokončená interakce → touched. `HiddenInput`
      // blur se prakticky nikdy nespustí (interakce jdou přes Dropzone/Trigger),
      // takže bez tohoto by se v módu `onTouched` validace nikdy nerozběhla.
      onBlur();
    }}
    className={clsx(styles.fileRoot, className)}
    ids={{ hiddenInput: id, label: labelId }}
  >
    <FileUpload.Dropzone className={styles.fileDropzone}>
      <Icon name="arrow-up-tray" size={28} />
      <span>{dropzoneLabel}</span>
    </FileUpload.Dropzone>
    {/* Trigger stojí mimo Dropzone — vnořené interaktivní role (button
        uvnitř role="button") jsou past pro klávesnici i AT. */}
    <FileUpload.Trigger className={styles.fileTrigger}>
      <Icon name="arrow-up-tray" size={16} />
      {triggerLabel}
    </FileUpload.Trigger>
    <FileUpload.ItemGroup className={styles.fileItemGroup}>
      <FileUpload.Context>
        {({ acceptedFiles }) =>
          acceptedFiles.map((file) => (
            <FileUpload.Item key={file.name} file={file} className={styles.fileItem}>
              <FileUpload.ItemName className={styles.fileItemName} />
              <FileUpload.ItemDeleteTrigger className={styles.fileDeleteTrigger}>
                <Icon name="x-mark" size={16} />
              </FileUpload.ItemDeleteTrigger>
            </FileUpload.Item>
          ))
        }
      </FileUpload.Context>
    </FileUpload.ItemGroup>
    <FileUpload.HiddenInput
      ref={ref}
      onBlur={onBlur}
      aria-describedby={describedBy}
      {...testAttributes}
    />
  </FileUpload.Root>
);

FileUploadInput.displayName = 'FileUploadInput';
